import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  SupportPriority,
  SupportTicketDetail,
  useAddSupportNoteMutation,
  useGetSavedRepliesQuery,
  useGetSupportMailboxQuery,
  useGetSupportSignaturesQuery,
  useGetSupportStatusesQuery,
  useGetSupportTicketQuery,
  useMarkIsCustomerMutation,
  useMarkNotCustomerMutation,
  usePreviewSupportReplyMutation,
  useRedraftSupportTicketMutation,
  useSaveSupportDraftMutation,
  useScheduleSupportReplyMutation,
  useSendSupportReplyMutation,
  useSetSupportPriorityMutation,
  useSetSupportStatusMutation,
} from "../../slices/SupportSlice";
import { shortDay, timeAgo } from "../settings/team/invites/teamInvites.utils";
import {
  CopiesFields,
  copiesFrom,
  CopiesLinks,
  CopiesState,
  NO_COPIES,
} from "./CopiesFields";
import { EmailPreviewModal } from "./EmailPreviewModal";
import { browserTimeZone, cleanCopies, formatSendTime } from "./schedule.utils";
import { ScheduledEmailCard } from "./ScheduledEmailCard";
import { ScheduleSendButton } from "./ScheduleSendButton";
import { SendTestBox } from "./SendTestBox";
import { SignaturePicker } from "./SignaturesCard";
import {
  caseStatusGroups,
  categoryPill,
  displayName,
  errorText,
  initialSignatureChoice,
  insertAt,
  MAX_REPLY,
  parseStatusValue,
  PILL_CLASSES,
  PRIORITIES,
  PRIORITY_LABELS,
  sendProblem,
  sourceLabel,
  statusBadge,
  statusPill,
  statusValue,
  STEP_LABELS,
} from "./support.utils";
import { PAGE, PAGE_INNER, PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";
import { WriteWithInstructions } from "./WriteWithInstructions";

/** One case: messages, notes, the AI summary / draft, Send (owner 2026-10-25; Help desk v2 the same day). */
export const SupportTicketPage = () => {
  const { ticketId = "" } = useParams();
  const navigate = useNavigate();
  const {
    data: ticket,
    isLoading,
    isError,
    error,
  } = useGetSupportTicketQuery(ticketId, { skip: !ticketId });
  const { data: box } = useGetSupportMailboxQuery();

  if (isLoading) return <LottieLoading />;
  if (isError || !ticket)
    return (
      <div className={PAGE}>
        <div className={PAGE_INNER}>
          <BackLink />
          <div className={"mt-8 text-center text-[15px] text-[#6B7185]"}>
            {errorText(error, "This case couldn't be loaded.")}
          </div>
        </div>
      </div>
    );
  return (
    <TicketBody
      key={ticket.id}
      ticket={ticket}
      mailboxConnected={!!box?.connected}
      aiConfigured={box?.aiConfigured !== false}
      onDone={() => navigate("/support")}
    />
  );
};

const BackLink = () => (
  <Link
    to={"/support"}
    className={"text-[13px] font-semibold text-[#6B7185] hover:text-[#6B4EFF]"}>
    ‹ Help desk
  </Link>
);

const SELECT =
  "h-9 rounded-lg border border-[#DCE2EA] bg-white px-2.5 text-[13.5px] font-semibold text-[#1F2433]";

const TicketBody = ({
  ticket,
  mailboxConnected,
  aiConfigured,
  onDone,
  inWindow = false,
}: {
  ticket: SupportTicketDetail;
  mailboxConnected: boolean;
  aiConfigured: boolean;
  onDone: () => void;
  /** The "Reply" window of the case list (owner 2026-10-30): no back link / customer card, draft written on open. */
  inWindow?: boolean;
}) => {
  const [text, setText] = useState(ticket.draft || "");
  const [savedDraft, setSavedDraft] = useState(ticket.draft || "");
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [note, setNote] = useState("");
  const [skipChoice, setSkipChoice] = useState<"none" | "email" | "domain">(
    "none",
  );
  const [askingNotCustomer, setAskingNotCustomer] = useState(false);
  const [instructing, setInstructing] = useState(false);
  const [testing, setTesting] = useState(false);
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const [send, { isLoading: isSending }] = useSendSupportReplyMutation();
  const [schedule, { isLoading: isScheduling }] =
    useScheduleSupportReplyMutation();
  const [
    runPreview,
    { isLoading: isPreviewing, data: preview, reset: clearPreview },
  ] = usePreviewSupportReplyMutation();
  const [saveDraft, { isLoading: isSaving }] = useSaveSupportDraftMutation();
  const [redraft, { isLoading: isRedrafting }] =
    useRedraftSupportTicketMutation();
  const [setStatus, { isLoading: isSettingStatus }] =
    useSetSupportStatusMutation();
  const [setPriority] = useSetSupportPriorityMutation();
  const [addNote, { isLoading: isNoting }] = useAddSupportNoteMutation();
  const [notCustomer, { isLoading: isMarking }] = useMarkNotCustomerMutation();
  const [isCustomer, { isLoading: isUnmarking }] = useMarkIsCustomerMutation();
  const { data: savedReplies } = useGetSavedRepliesQuery();
  const { data: signatures } = useGetSupportSignaturesQuery();
  // The signature under this email: the default one until the owner picks another (or "No signature").
  const [signatureChoice, setSignatureChoice] = useState<string | null>(null);
  const signature =
    signatureChoice ?? initialSignatureChoice(signatures?.signatures);
  const { data: statuses } = useGetSupportStatusesQuery();

  // A new AI draft (Try again, or the customer wrote again) replaces the box unless the owner is typing.
  useEffect(() => {
    const server = ticket.draft || "";
    if (server !== savedDraft) {
      if (text === savedDraft) setText(server);
      setSavedDraft(server);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.draft]);

  const pill = categoryPill(ticket.category);
  const status = statusBadge(ticket);
  const ownStatuses = statuses?.caseStatuses || [];
  const isOther = ticket.status === "other";
  const edited = text.trim() !== savedDraft.trim();
  const fromAi = !!ticket.draft && !edited;

  // A draft ProCharted made (new firm email, demo follow-up): sent as a new email, then Solved (owner 2026-10-26).
  const firstOfOurs = isOurFirstEmail(ticket);
  // Our own first email goes out through SendGrid when support@ isn't connected (the server allows it).
  // Cc / Bcc (owner 2026-11-03), checked like the server; a cancelled scheduled email brings its copies back.
  const [copies, setCopies] = useState<CopiesState>(() =>
    copiesFrom(ticket.draftCopies?.cc, ticket.draftCopies?.bcc),
  );
  useEffect(() => {
    if (ticket.draftCopies && !copies.cc && !copies.bcc)
      setCopies(copiesFrom(ticket.draftCopies.cc, ticket.draftCopies.bcc));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.draftCopies]);
  const cleaned = cleanCopies([ticket.contactEmail], copies.cc, copies.bcc);
  const copiesToSend = { cc: cleaned.cc, bcc: cleaned.bcc };
  const problem =
    sendProblem(text, mailboxConnected || firstOfOurs) || cleaned.problem;
  const signatureToSend = signatures?.available ? signature : undefined;

  // "Preview" (owner 2026-10-29): the server builds the email exactly as Send would.
  const doPreview = async () => {
    if (!text.trim()) return;
    try {
      await runPreview({
        id: ticket.id,
        text,
        then: "solved",
        signature: signatureToSend,
        ...copiesToSend,
      }).unwrap();
    } catch (e) {
      toast(errorText(e, "The preview couldn't be made. Please try again."), {
        type: "error",
      });
    }
  };
  const doSend = async (then: "waiting" | "solved") => {
    if (problem) return;
    try {
      await send({
        id: ticket.id,
        text,
        then,
        signature: signatureToSend,
        ...copiesToSend,
      }).unwrap();
      clearPreview();
      setCopies(NO_COPIES);
      toast(
        then === "solved"
          ? `Reply sent to ${ticket.contactEmail}. Case solved.`
          : `Reply sent to ${ticket.contactEmail}. Waiting for their answer.`,
        { type: "success" },
      );
      onDone();
    } catch (e) {
      toast(errorText(e, "The reply wasn't sent. Please try again."), {
        type: "error",
      });
    }
  };

  // "Schedule send" (owner 2026-11-03): the text is kept with the scheduled email and the box empties.
  const doSchedule = async (at: Date) => {
    if (problem) return;
    try {
      const updated = await schedule({
        id: ticket.id,
        text,
        then: firstOfOurs ? "solved" : "waiting",
        signature: signatureToSend,
        ...copiesToSend,
        sendAt: at.toISOString(),
        timeZone: browserTimeZone(),
      }).unwrap();
      setText("");
      setSavedDraft("");
      setCopies(NO_COPIES);
      toast(
        `Scheduled for ${formatSendTime(
          updated.scheduled?.sendAt || at,
        )}. It goes out by itself.`,
        { type: "success" },
      );
    } catch (e) {
      toast(errorText(e, "The email wasn't scheduled. Please try again."), {
        type: "error",
      });
    }
  };

  const doSave = async (value: string) => {
    try {
      await saveDraft({ id: ticket.id, text: value }).unwrap();
      setSavedDraft(value.trim());
    } catch (e) {
      toast(errorText(e, "The draft wasn't saved."), { type: "error" });
    }
  };

  const doRedraft = async () => {
    try {
      const updated = await redraft(ticket.id).unwrap();
      setText(updated.draft || "");
      setSavedDraft(updated.draft || "");
      if (!updated.draft)
        toast(
          updated.aiSummary ||
            "The AI had nothing safe to write for this one. Please write it yourself.",
          {
            type: "info",
          },
        );
    } catch (e) {
      toast(errorText(e, "The AI couldn't write a draft. Please try again."), {
        type: "error",
      });
    }
  };

  // "Write with instructions" (owner 2026-10-27): the box's own text is saved first, so "make it shorter" works on it.
  const doInstructed = async (instructions: string) => {
    try {
      if (edited) await saveDraft({ id: ticket.id, text }).unwrap();
      const updated = await redraft({ id: ticket.id, instructions }).unwrap();
      setText(updated.draft || "");
      setSavedDraft(updated.draft || "");
      setInstructing(false);
      setMode("reply");
      if (!updated.draft)
        toast("The AI couldn't write that one. Please write it yourself.", {
          type: "info",
        });
    } catch (e) {
      toast(errorText(e, "The AI couldn't write a draft. Please try again."), {
        type: "error",
      });
    }
  };

  const doStatus = async (value: string) => {
    const next = parseStatusValue(value, ownStatuses);
    if (!next) return;
    try {
      await setStatus({ id: ticket.id, ...next }).unwrap();
      if (next.status === "solved" || next.status === "closed") onDone();
    } catch (e) {
      toast(errorText(e, "Couldn't change it. Please try again."), {
        type: "error",
      });
    }
  };

  const doPriority = async (next: SupportPriority) => {
    try {
      await setPriority({ id: ticket.id, priority: next }).unwrap();
    } catch (e) {
      toast(errorText(e, "Couldn't change it. Please try again."), {
        type: "error",
      });
    }
  };

  const doNote = async () => {
    if (!note.trim()) return;
    try {
      await addNote({ id: ticket.id, text: note }).unwrap();
      setNote("");
      toast("Note added (only you can see it).", { type: "success" });
    } catch (e) {
      toast(errorText(e, "The note wasn't saved."), { type: "error" });
    }
  };

  const doNotCustomer = async () => {
    try {
      await notCustomer({ id: ticket.id, skip: skipChoice }).unwrap();
      toast(
        skipChoice === "none"
          ? "Moved to Other mail."
          : `Moved to Other mail. ${
              skipChoice === "domain"
                ? "Everyone at that domain"
                : "This sender"
            } is skipped from now on.`,
        { type: "success" },
      );
      onDone();
    } catch (e) {
      toast(errorText(e, "Couldn't move it. Please try again."), {
        type: "error",
      });
    }
  };

  const doIsCustomer = async () => {
    try {
      await isCustomer(ticket.id).unwrap();
      toast("Moved to Needs you.", { type: "success" });
    } catch (e) {
      toast(errorText(e, "Couldn't move it. Please try again."), {
        type: "error",
      });
    }
  };

  const insertSaved = (id: string) => {
    const reply = savedReplies?.find((r) => r.id === id);
    if (!reply) return;
    const el = replyRef.current;
    const at = el ? el.selectionStart ?? text.length : text.length;
    const to = el ? el.selectionEnd ?? text.length : text.length;
    const next = text.trim()
      ? insertAt(text, at, to, reply.body)
      : { text: reply.body, cursor: reply.body.length };
    setText(next.text);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(next.cursor, next.cursor);
    });
  };

  // The Reply window opens with an answer already written (owner 2026-10-30): when the case has no draft
  // yet, the AI writes one. Never for cases the AI must not answer on its own (cancellations, billing…),
  // Other mail, or our own automatic emails.
  const autoDrafted = useRef(false);
  useEffect(() => {
    if (
      !inWindow ||
      autoDrafted.current ||
      !aiConfigured ||
      ticket.draft ||
      text.trim() ||
      ticket.ownerOnly ||
      isOther ||
      ticket.source === "onboarding"
    )
      return;
    autoDrafted.current = true;
    void doRedraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inWindow]);

  const domain = ticket.contactEmail.split("@")[1] || "";

  return (
    <div className={inWindow ? "" : PAGE}>
      <div
        className={
          inWindow
            ? "min-w-0"
            : classNames(
                PAGE_INNER,
                "grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_320px]",
              )
        }>
        <div className={"min-w-0"}>
          {!inWindow && <BackLink />}
          <div
            className={"mt-2 flex flex-wrap items-start justify-between gap-3"}>
            <div className={"min-w-0"}>
              <div
                className={
                  "flex flex-wrap items-center gap-2 text-[13px] text-[#5D6475]"
                }>
                <span
                  className={
                    "font-mono text-[13.5px] font-semibold text-[#5D6475]"
                  }>
                  Case #{ticket.code}
                </span>
                <span
                  className={classNames(
                    "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
                    PILL_CLASSES[status.tone],
                  )}>
                  {status.text}
                </span>
                <span
                  className={classNames(
                    "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
                    PILL_CLASSES[pill.tone],
                  )}>
                  {pill.text}
                </span>
                <span>{sourceLabel(ticket.source)}</span>
                {ticket.rating && (
                  <span
                    className={classNames(
                      "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
                      ticket.rating === "good"
                        ? PILL_CLASSES.green
                        : PILL_CLASSES.red,
                    )}>
                    Rated {ticket.rating}
                  </span>
                )}
              </div>
              <h1
                className={
                  "mb-1.5 mt-2 break-words text-[22px] font-extrabold tracking-[-0.4px] text-[#1F2433]"
                }>
                {ticket.subject || "(no subject)"}
              </h1>
            </div>
            {!isOther && (
              <div className={"flex gap-2"}>
                <label
                  className={
                    "flex flex-col gap-1 text-[12px] font-bold text-[#5D6475]"
                  }>
                  Status
                  <select
                    value={statusValue({
                      ...ticket,
                      customStatusId: ownStatuses.some(
                        (s) => s.id === ticket.customStatusId,
                      )
                        ? ticket.customStatusId
                        : null,
                    })}
                    disabled={isSettingStatus}
                    onChange={(e) => void doStatus(e.target.value)}
                    className={SELECT}>
                    {caseStatusGroups(ownStatuses).map((g) =>
                      g.options.length === 1 ? (
                        <option key={g.group} value={g.options[0].value}>
                          {g.options[0].label}
                        </option>
                      ) : (
                        <optgroup key={g.group} label={g.label}>
                          {g.options.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </optgroup>
                      ),
                    )}
                  </select>
                </label>
                <label
                  className={
                    "flex flex-col gap-1 text-[12px] font-bold text-[#5D6475]"
                  }>
                  Priority
                  <select
                    value={ticket.priority}
                    onChange={(e) =>
                      void doPriority(e.target.value as SupportPriority)
                    }
                    className={SELECT}>
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_LABELS[p].text}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>

          {(isOther || ticket.notSure) && (
            <div
              className={
                "mb-3 mt-1 rounded-xl border border-[#F5DEB0] bg-[#FFF9EE] px-3.5 py-3 text-[13.5px] leading-relaxed text-[#7A5A12]"
              }>
              {isOther ? (
                <>
                  <b>Other mail.</b> This doesn&apos;t look like a customer (an
                  invoice, newsletter, robot or a sender you skip), so it
                  isn&apos;t in Needs you and got no automatic reply.
                </>
              ) : (
                <>
                  <b>Not sure this is a customer.</b> The AI couldn&apos;t tell,
                  so it&apos;s in Needs you and got no automatic reply.
                </>
              )}
              <div className={"mt-2.5 flex flex-wrap items-center gap-2"}>
                <button
                  type={"button"}
                  disabled={isUnmarking}
                  onClick={() => void doIsCustomer()}
                  className={SMALL_BUTTON}>
                  This is a customer
                </button>
                {!isOther &&
                  (askingNotCustomer ? null : (
                    <button
                      type={"button"}
                      onClick={() => setAskingNotCustomer(true)}
                      className={SMALL_BUTTON}>
                      Not a customer…
                    </button>
                  ))}
              </div>
            </div>
          )}

          {(askingNotCustomer || (isOther && !ticket.notSure)) && (
            <div
              className={
                "mb-3 rounded-xl border border-[#EDEFF3] bg-white px-3.5 py-3 text-[13.5px] text-[#3A4256]"
              }>
              <div className={"mb-2 font-bold text-[#1F2433]"}>
                {isOther
                  ? "Skip this sender from now on?"
                  : "Move to Other mail"}
              </div>
              <div className={"flex flex-col gap-1.5"}>
                {(
                  [
                    [
                      "none",
                      isOther ? "No, just leave it here" : "Just this email",
                    ],
                    ["email", `Always skip ${ticket.contactEmail}`],
                    ["domain", `Always skip everyone @${domain}`],
                  ] as const
                ).map(([value, label]) => (
                  <label key={value} className={"flex items-center gap-2"}>
                    <input
                      type={"radio"}
                      name={"skip"}
                      checked={skipChoice === value}
                      onChange={() => setSkipChoice(value)}
                      className={"accent-[#6B4EFF]"}
                    />
                    {label}
                  </label>
                ))}
              </div>
              <div className={"mt-2.5 flex gap-2"}>
                <button
                  type={"button"}
                  disabled={isMarking || (isOther && skipChoice === "none")}
                  onClick={() => void doNotCustomer()}
                  className={PRIMARY_BUTTON}>
                  {isOther ? "Save" : "Move to Other mail"}
                </button>
                {askingNotCustomer && (
                  <button
                    type={"button"}
                    onClick={() => setAskingNotCustomer(false)}
                    className={SMALL_BUTTON}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}

          {ticket.ownerOnly && !isOther && (
            <div
              className={
                "mb-3 rounded-xl border border-[#F5DEB0] bg-[#FFF6E5] px-3.5 py-3 text-[13.5px] leading-relaxed text-[#7A5A12]"
              }>
              <b>This one is yours.</b> Cancellations, billing, privacy requests
              and bug reports are never answered by the AI on its own. It only
              summarises them for you
              {aiConfigured ? (
                <>
                  ; click <b>Help me write it</b> if you&apos;d like a starting
                  point.
                </>
              ) : (
                "."
              )}
            </div>
          )}

          {ticket.aiSummary && (
            <div
              className={
                "mb-3 rounded-xl border border-[#EDEFF3] bg-[#FAFAFB] px-4 py-3 text-[13.5px] leading-relaxed text-[#3A4256]"
              }>
              <div className={"mb-1 text-[12.5px] font-bold text-[#4B32D6]"}>
                {ticket.source === "onboarding"
                  ? "📝 Note (not sent)"
                  : "✨ AI summary (not sent)"}
              </div>
              {ticket.aiSummary}
            </div>
          )}

          <div
            className={"mb-2 flex gap-1"}
            role={"tablist"}
            aria-label={"Reply or private note"}>
            {(
              [
                ["reply", "Reply to customer"],
                ["note", "Private note"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type={"button"}
                role={"tab"}
                aria-selected={mode === value}
                onClick={() => setMode(value)}
                className={classNames(
                  "h-8 rounded-lg px-3 text-[13px]",
                  mode === value
                    ? "bg-[#EFEBFF] font-bold text-[#4B32D6]"
                    : "font-semibold text-[#5D6475] hover:text-[#1F2433]",
                )}>
                {label}
              </button>
            ))}
            {mode === "reply" && (savedReplies?.length ?? 0) > 0 && (
              <>
                <span className={"flex-1"} />
                <label className={"sr-only"} htmlFor={"saved-reply-menu"}>
                  Insert a saved reply
                </label>
                <select
                  id={"saved-reply-menu"}
                  value={""}
                  onChange={(e) => insertSaved(e.target.value)}
                  className={classNames(SELECT, "h-8 text-[13px]")}>
                  <option value={""}>Insert saved reply ▾</option>
                  {savedReplies!.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.title}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          {mode === "note" ? (
            <div
              className={
                "mb-3 rounded-xl border-[1.5px] border-[#F5DEB0] bg-[#FFFDF5] px-4 py-3"
              }>
              <label
                htmlFor={"support-note"}
                className={"mb-1.5 block text-[12.5px] text-[#7A5A12]"}>
                <b>Private note</b> · only on the Help desk, never emailed,
                never shown to the AI
              </label>
              <textarea
                id={"support-note"}
                value={note}
                maxLength={10000}
                rows={4}
                placeholder={"e.g. Checked his account: locked for 15 minutes."}
                onChange={(e) => setNote(e.target.value)}
                className={
                  "w-full resize-y border-0 bg-transparent text-[14px] leading-relaxed text-[#1F2433] outline-none"
                }
              />
              <button
                type={"button"}
                disabled={!note.trim() || isNoting}
                onClick={() => void doNote()}
                className={classNames(PRIMARY_BUTTON, "mt-1")}>
                {isNoting ? "Saving…" : "Add note"}
              </button>
            </div>
          ) : (
            <>
              <ScheduledEmailCard ticket={ticket} />
              {instructing && (
                <WriteWithInstructions
                  busy={isRedrafting || isSaving}
                  onWrite={(instructions) => void doInstructed(instructions)}
                  onCancel={() => setInstructing(false)}
                  note={
                    text.trim()
                      ? "Your current draft is given to the AI too, so you can also ask for changes (e.g. “make it shorter”)."
                      : undefined
                  }
                />
              )}
              <div
                className={classNames(
                  "mb-3 rounded-xl border-[1.5px] bg-white px-4 py-3",
                  fromAi ? "border-[#6B4EFF] bg-[#FBFAFF]" : "border-[#DCE2EA]",
                )}>
                <div
                  className={"mb-1.5 flex items-start justify-between gap-3"}>
                  <label
                    htmlFor={"support-reply"}
                    className={"block text-[12.5px] text-[#7A7F8C]"}>
                    {fromAi ? (
                      <>
                        <b className={"text-[#4B32D6]"}>
                          {ticket.source === "onboarding"
                            ? "📝 Draft from your automatic email"
                            : "✨ AI draft"}
                        </b>{" "}
                        · you can change anything before sending
                      </>
                    ) : (
                      <>
                        <b className={"text-[#1F2433]"}>Your reply</b> to{" "}
                        {ticket.contactEmail}
                      </>
                    )}
                  </label>
                  <CopiesLinks value={copies} onChange={setCopies} />
                </div>
                {(copies.showCc || copies.showBcc) && (
                  <div className={"mb-2 flex flex-col gap-2"}>
                    <CopiesFields
                      value={copies}
                      onChange={setCopies}
                      idPrefix={"reply"}
                    />
                  </div>
                )}
                <textarea
                  id={"support-reply"}
                  ref={replyRef}
                  value={text}
                  maxLength={MAX_REPLY}
                  rows={Math.min(18, Math.max(7, text.split("\n").length + 1))}
                  placeholder={"Write your reply…"}
                  onChange={(event) => setText(event.target.value)}
                  onBlur={() => edited && void doSave(text)}
                  className={
                    "w-full resize-y border-0 bg-transparent text-[14px] leading-relaxed text-[#1F2433] outline-none"
                  }
                />
                <SignaturePicker
                  value={signature}
                  onChange={setSignatureChoice}
                />
                {fromAi && ticket.draftSources.length > 0 && (
                  <div className={"mt-1 flex flex-wrap gap-1.5"}>
                    {ticket.draftSources.map((source) => (
                      <a
                        key={source.url}
                        href={source.url}
                        target={"_blank"}
                        rel={"noreferrer"}
                        className={
                          "rounded-lg bg-[#F1F2F5] px-2.5 py-1 text-[12px] text-[#3A4256] hover:text-[#6B4EFF]"
                        }>
                        From: {source.title}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className={"flex flex-wrap gap-2"}>
                <button
                  type={"button"}
                  disabled={!!problem || isSending}
                  title={problem || undefined}
                  onClick={() => void doSend("solved")}
                  className={classNames(
                    PRIMARY_BUTTON,
                    "h-10 px-5 text-[14px]",
                  )}>
                  {isSending
                    ? "Sending…"
                    : firstOfOurs
                    ? "Send"
                    : "Send and mark Solved"}
                </button>
                {!firstOfOurs && (
                  <button
                    type={"button"}
                    disabled={!!problem || isSending}
                    title={problem || undefined}
                    onClick={() => void doSend("waiting")}
                    className={classNames(SMALL_BUTTON, "h-10")}>
                    Send and wait for customer
                  </button>
                )}
                <ScheduleSendButton
                  disabled={!!problem || isSending || !!ticket.scheduled}
                  busy={isScheduling}
                  title={
                    ticket.scheduled
                      ? "This case already has a scheduled email. Change or cancel it first."
                      : problem ||
                        "Send it later, at a time you pick (it goes out by itself)"
                  }
                  onSchedule={(at) => void doSchedule(at)}
                />
                <button
                  type={"button"}
                  disabled={!text.trim() || isPreviewing}
                  title={
                    text.trim()
                      ? "See the email exactly as it will be sent"
                      : "Write a reply first."
                  }
                  onClick={() => void doPreview()}
                  className={classNames(SMALL_BUTTON, "h-10")}
                  data-testid={"preview-email"}>
                  {isPreviewing ? "Opening…" : "👁 Preview"}
                </button>
                <button
                  type={"button"}
                  disabled={!text.trim()}
                  title={
                    text.trim()
                      ? "Send this email to yourself (or anyone) as a test"
                      : "Write a reply first."
                  }
                  onClick={() => setTesting(true)}
                  className={classNames(SMALL_BUTTON, "h-10")}
                  data-testid={"send-test-button"}>
                  ✉️ Send test
                </button>
                {aiConfigured && ticket.source !== "onboarding" && (
                  <button
                    type={"button"}
                    disabled={isRedrafting}
                    onClick={() => void doRedraft()}
                    className={classNames(SMALL_BUTTON, "h-10")}>
                    {isRedrafting
                      ? "Writing…"
                      : ticket.ownerOnly
                      ? "✨ Help me write it"
                      : "✨ Try again"}
                  </button>
                )}
                {aiConfigured && !instructing && (
                  <button
                    type={"button"}
                    disabled={isRedrafting}
                    onClick={() => setInstructing(true)}
                    className={classNames(SMALL_BUTTON, "h-10")}
                    data-testid={"write-with-instructions"}>
                    ✍️ Write with instructions
                  </button>
                )}
                {edited && (
                  <button
                    type={"button"}
                    disabled={isSaving}
                    onClick={() => void doSave(text)}
                    className={classNames(SMALL_BUTTON, "h-10")}>
                    {isSaving ? "Saving…" : "Save draft"}
                  </button>
                )}
                {!!text && (
                  <button
                    type={"button"}
                    onClick={() => {
                      setText("");
                      void doSave("");
                    }}
                    className={classNames(SMALL_BUTTON, "h-10 text-[#C0392B]")}>
                    Discard draft
                  </button>
                )}
              </div>
              <div className={"mt-2 text-[12.5px] text-[#5D6475]"}>
                {firstOfOurs ? (
                  <>
                    Goes out as a new email from support@ with the subject
                    &ldquo;{ticket.subject}&rdquo;. The case is then Solved; if
                    they reply, it opens again.
                  </>
                ) : (
                  <>
                    Solved cases close by themselves after 7 days; if the
                    customer writes back, the case opens again.
                  </>
                )}
              </div>
              {testing && (
                <SendTestBox
                  ticketId={ticket.id}
                  text={text}
                  then={"solved"}
                  signature={signatureToSend}
                  onClose={() => setTesting(false)}
                />
              )}
              {preview && (
                <EmailPreviewModal
                  preview={preview}
                  onClose={clearPreview}
                  sendLabel={
                    isSending
                      ? "Sending…"
                      : firstOfOurs
                      ? "Send"
                      : "Send and mark Solved"
                  }
                  sendDisabled={!!problem || isSending}
                  onSend={() => void doSend("solved")}
                  note={
                    "This is exactly what they will get. Nothing has been sent."
                  }
                />
              )}
              {problem && text.trim() && (
                <div className={"mt-2 text-[12.5px] text-[#9A5B00]"}>
                  {problem}
                </div>
              )}
            </>
          )}

          {ticket.messages.length > 0 && (
            <h2
              className={
                "mb-2 mt-6 text-[13px] font-bold uppercase tracking-[.5px] text-[#5D6475]"
              }>
              Conversation
            </h2>
          )}
          {ticket.messages.map((message) => (
            <div
              key={message.id}
              className={classNames(
                "mb-3 rounded-xl border px-4 py-3.5 text-[14px] leading-relaxed",
                message.direction === "note"
                  ? "border-[#F5DEB0] bg-[#FFF8E1]"
                  : message.automatic
                  ? "border-dashed border-[#C9D1DD] bg-[#F6F8FB] sm:ml-10"
                  : message.direction === "out"
                  ? "ml-0 border-[#E4DEFF] bg-[#F8F6FF] sm:ml-10"
                  : "border-[#EDEFF3] bg-white",
              )}>
              <div className={"mb-1.5 text-[12.5px] text-[#7A7F8C]"}>
                {message.direction === "note" ? (
                  <b className={"text-[#5A4300]"}>
                    Private note · {message.sentByEmail} (the customer
                    can&apos;t see this)
                  </b>
                ) : message.automatic ? (
                  <b className={"text-[#3A4256]"}>Sent automatically</b>
                ) : message.direction === "out" ? (
                  <>
                    <b className={"text-[#4B32D6]"}>ProCharted</b>
                    {message.sentByEmail
                      ? ` · sent by ${message.sentByEmail}`
                      : ""}
                    {message.cc ? ` · cc ${message.cc}` : ""}
                    {message.bcc ? ` · bcc ${message.bcc}` : ""}
                  </>
                ) : (
                  <>
                    <b className={"text-[#1F2433]"}>
                      {message.fromName || displayName(ticket)}
                    </b>
                    {message.fromEmail ? ` <${message.fromEmail}>` : ""}
                  </>
                )}{" "}
                · {timeAgo(message.at)}
              </div>
              <div className={"whitespace-pre-wrap break-words text-[#1F2433]"}>
                {message.body}
              </div>
            </div>
          ))}
        </div>

        {!inWindow && <CustomerCard ticket={ticket} />}
      </div>
    </div>
  );
};

/**
 * "Reply" window opened from the Help desk case list (owner 2026-10-30, Loom dad3c816): the case's own reply
 * area (AI draft written on open, Send, Preview, Write with instructions, Discard draft, signature, private
 * note) with the conversation under it, without leaving the list.
 */
export const SupportReplyWindow = ({
  ticketId,
  onClose,
}: {
  ticketId: string;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const {
    data: ticket,
    isLoading,
    isError,
    error,
  } = useGetSupportTicketQuery(ticketId, { skip: !ticketId });
  const { data: box } = useGetSupportMailboxQuery();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // The Preview window inside handles its own Esc.
      if (
        e.key === "Escape" &&
        !document.querySelector("[data-testid='email-preview']")
      )
        onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className={
        "fixed inset-0 z-[900] flex items-start justify-center overflow-y-auto bg-[#1F2433]/40 px-3 py-8"
      }
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      data-testid={"reply-window"}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Reply to customer"}
        className={
          "w-full max-w-[860px] rounded-[16px] bg-white shadow-[0_20px_60px_rgba(31,36,51,0.25)]"
        }>
        <div
          className={
            "flex items-center justify-between gap-3 border-b border-[#EDEFF3] px-6 py-3.5"
          }>
          <div className={"text-[16px] font-extrabold text-[#1F2433]"}>
            Reply
          </div>
          <div className={"flex items-center gap-2"}>
            <button
              type={"button"}
              onClick={() => navigate(`/support/${ticketId}`)}
              className={classNames(SMALL_BUTTON, "h-8")}>
              Open full case
            </button>
            <button
              type={"button"}
              onClick={onClose}
              aria-label={"Close"}
              className={
                "flex h-8 w-8 items-center justify-center rounded-full text-[20px] leading-none text-[#5D6475] hover:bg-[#F4F5F8]"
              }>
              ×
            </button>
          </div>
        </div>
        <div className={"px-6 py-4"}>
          {isLoading ? (
            <LottieLoading />
          ) : isError || !ticket ? (
            <div className={"py-8 text-center text-[14px] text-[#6B7185]"}>
              {errorText(error, "This case couldn't be loaded.")}
            </div>
          ) : (
            <TicketBody
              key={ticket.id}
              ticket={ticket}
              mailboxConnected={!!box?.connected}
              aiConfigured={box?.aiConfigured !== false}
              onDone={onClose}
              inWindow
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const CustomerCard = ({ ticket }: { ticket: SupportTicketDetail }) => {
  const c = ticket.customer;
  const row = (label: string, value?: React.ReactNode) =>
    value ? (
      <div className={"flex justify-between gap-3 py-1 text-[13px]"}>
        <span className={"text-[#7A7F8C]"}>{label}</span>
        <span className={"text-right text-[#1F2433]"}>{value}</span>
      </div>
    ) : null;
  return (
    <aside
      className={
        "h-fit rounded-[14px] border border-[#EDEFF3] bg-white px-[18px] py-4 lg:mt-7"
      }>
      <div className={"font-bold text-[#1F2433]"}>{displayName(ticket)}</div>
      <div className={"break-all text-[12.5px] text-[#7A7F8C]"}>
        {ticket.contactEmail}
      </div>
      <h4 className={HEADING}>Their firm</h4>
      {c.isUser ? (
        <>
          {row("Firm", c.firmName)}
          {row("Role", c.role)}
          {row("People", c.people != null ? String(c.people) : undefined)}
          {row("Plan", c.plan)}
          {row(
            "Status",
            c.planStatus ? (
              <span
                className={classNames(
                  "rounded-full px-2 py-0.5 text-[12px] font-bold",
                  c.planStatus === "active" || c.planStatus === "trialing"
                    ? PILL_CLASSES.green
                    : PILL_CLASSES.amber,
                )}>
                {c.planStatus}
              </span>
            ) : undefined,
          )}
          {row(
            "Customer since",
            c.customerSince
              ? new Date(c.customerSince).toLocaleDateString("en-US", {
                  month: "short",
                  year: "numeric",
                })
              : undefined,
          )}
        </>
      ) : c.inStripe ? (
        <>
          <div className={"mb-1 text-[13px] text-[#1F2433]"}>
            Found as a customer in Stripe (not a ProCharted sign-in address).
          </div>
          {row("Firm", c.firmName)}
        </>
      ) : (
        <div className={"text-[13px] text-[#7A7F8C]"}>
          This address isn&apos;t a ProCharted user or a Stripe customer (maybe
          someone asking before signing up).
        </div>
      )}
      {c.onboarding && (
        <>
          <h4 className={HEADING}>Getting started</h4>
          {c.onboarding.trialDay != null && (
            <div className={"text-[13px] text-[#1F2433]"}>
              Free trial · day {c.onboarding.trialDay} of 14
            </div>
          )}
          <div className={"mt-1 flex flex-wrap gap-1"}>
            {STEP_LABELS.map((step) => (
              <span
                key={step.key}
                className={classNames(
                  "rounded-full px-2 py-0.5 text-[11.5px] font-semibold",
                  c.onboarding!.steps[step.key]
                    ? PILL_CLASSES.green
                    : PILL_CLASSES.grey,
                )}>
                {c.onboarding!.steps[step.key] ? "✓ " : "○ "}
                {step.label}
              </span>
            ))}
          </div>
          <div className={"mt-1.5 text-[12.5px] text-[#5D6475]"}>
            {[
              c.onboarding.welcomeSentAt &&
                `Welcome ${shortDay(c.onboarding.welcomeSentAt)}`,
              c.onboarding.checkinSentAt &&
                `check-in ${shortDay(c.onboarding.checkinSentAt)}`,
              c.onboarding.trialEndingSentAt &&
                `trial ending ${shortDay(c.onboarding.trialEndingSentAt)}`,
            ]
              .filter(Boolean)
              .join(" · ") || "No onboarding emails yet"}
          </div>
        </>
      )}
      {ticket.earlier.length > 0 && (
        <>
          <h4 className={HEADING}>Earlier cases</h4>
          {ticket.earlier.map((e) => (
            <Link
              key={e.id}
              to={`/support/${e.id}`}
              className={
                "flex justify-between gap-3 py-1 text-[13px] hover:text-[#6B4EFF]"
              }>
              <span className={"shrink-0 text-[#7A7F8C]"}>
                {shortDay(e.at)}
              </span>
              <span className={"truncate text-right"}>
                {e.subject || "(no subject)"} · {statusPill(e.status).text}
                {e.rating ? ` · rated ${e.rating}` : ""}
              </span>
            </Link>
          ))}
        </>
      )}
      <h4 className={HEADING}>
        {ticket.source === "onboarding"
          ? "Where the draft came from"
          : "What the AI used"}
      </h4>
      <div className={"text-[12.5px] text-[#7A7F8C]"}>
        {ticket.source === "onboarding"
          ? "Your automatic email (Help desk › Automatic emails) with their name and firm filled in. No AI."
          : ticket.source === "demo"
          ? "Your notes on the demo's work item and your Demo follow-up email (Automatic emails). It never sends anything: you check the draft and click Send."
          : "Only ProCharted's help articles, your saved replies and this conversation (not your private notes). It never sees the firm's clients or work, and it never sends anything."}
      </div>
    </aside>
  );
};

/** A draft ProCharted made for the owner that hasn't been sent yet (no email from them in the case). */
export const isOurFirstEmail = (
  t: Pick<SupportTicketDetail, "source" | "firstReplyAt" | "messages">,
) =>
  (t.source === "onboarding" || t.source === "demo") &&
  !t.firstReplyAt &&
  !t.messages.some((m) => m.direction === "in");

const HEADING =
  "mb-1.5 mt-4 text-[11.5px] font-bold uppercase tracking-[0.06em] text-[#7A7F8C]";
